import { inject, InjectionToken } from "@angular/core";

/**
 * Controls how intermediate reasoning messages are displayed.
 *
 * - `streaming-clear` *(default)*: each step shows with a spinner; when superseded it
 *   receives the `--done` class (checkmark + CSS fade-out).
 * - `last-only`: only the currently-active incomplete step is shown; completed steps
 *   are hidden immediately.
 * - `hidden`: no reasoning steps are rendered; only final markdown / rich content.
 * - `persist`: all steps remain visible with no `--done` class — nothing fades out.
 */
export type ReasoningDisplay = "streaming-clear" | "last-only" | "hidden" | "persist";

/**
 * Per-chat display configuration token.
 * Provide at the chat host component level to customise which UI elements are
 * shown inside AgentChatAssistantMessage without requiring prop-drilling.
 *
 * @example
 * providers: [{ provide: ACHAT_VIEW_CONFIG, useValue: { showFeedbackButtons: false } }]
 */
export interface AchatViewConfig {
  /** Whether to show thumbs-up / thumbs-down feedback buttons. Defaults to true. */
  showFeedbackButtons?: boolean;

  /**
   * How intermediate reasoning steps are displayed.
   * Defaults to `'streaming-clear'` (current behaviour: show spinner, fade on completion).
   */
  reasoningDisplay?: ReasoningDisplay;
}

export const ACHAT_VIEW_CONFIG = new InjectionToken<AchatViewConfig>("ACHAT_VIEW_CONFIG");

export function injectAchatViewConfig(): AchatViewConfig | null {
  return inject(ACHAT_VIEW_CONFIG, { optional: true });
}
